import { Base } from '@studiometa/js-toolkit';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger.js';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin.js';
import HotspotGroup from './HotspotGroup.js';

gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(ScrollToPlugin);

/**
 * ScrollVideo - Contrôle la lecture d'une vidéo en fonction du scroll
 */
export default class ScrollVideo extends Base {
  breakpoints = ['xxs', 'xs', 's'];
  scrollIsStarted = false;

  /**
   * Component Configuration
   */
  static config = {
    name: 'ScrollVideo',
    refs: ['video', 'videoMobile'],
    components: {
      HotspotGroup,
    },
  };

  /**
   *
   */
  mounted() {
    this.hotspotGroups = this.$children.HotspotGroup;

    this.tl = gsap.timeline({
      scrollTrigger: {
        trigger: this.$el,
        start: 'top top',
        end: '+=300%',
        pin: true,
        scrub: true,
      },
    });

    if (this.isMobile) {
      this.initMobileTimeline();
    } else {
      this.initDesktopTimeline();
    }
  }

  /**
   *
   */
  initDesktopTimeline() {
    if (!this.$refs.video) {
      return;
    }

    const videoTotalDuration = this.$refs.video.duration;
    this.hotspotGroups.forEach((element, index) => {
      if (index === 0) {
        this.tl.fromTo(
          this.$refs.video,
          {
            currentTime: 0,
          },
          {
            currentTime: element.position,
            onUpdate: () => {
              if (this.$refs.video.currentTime < element.position && !element.isHide) {
                gsap.to(element.hotspotsBtn, {
                  opacity: 0,
                  scale: 0,
                  ease: 'power4.out',
                  stagger: 0.05,
                });
                element.isHide = true;
                element.disableGroup();
              }
              if (this.$refs.video.currentTime >= element.position && element.isHide !== false) {
                element.isHide = false;
                element.enableGroup();
                gsap.to(element.hotspotsBtn, {
                  opacity: 1,
                  scale: 1,
                  ease: 'power4.out',
                  stagger: 0.05,
                });
              }
            },
          },
        );
      }

      element.hotspotsContent.forEach((el, i) => {
        if (element.hotspotsContent.length - 1 === i) {
          this.tl.fromTo(el, { yPercent: 100 }, { yPercent: -15 });
          this.tl.addLabel(el.id);

          // Tricks when clicking on btn and set timeline to good position
          this.tl.to('#null', {
            display: 'block',
            duration: 0.2,
            onComplete: () => {
              gsap.to(element.hotspotsBtn, {
                opacity: 0,
                scale: 0,
                ease: 'power4.out',
                stagger: 0.05,
              });
              gsap.to(element.hotspotsContent, {
                rotate: -45,
                xPercent: -100,
                ease: 'power4.out',
                stagger: 0.1,
                duration: 0.4,
              });
            },
          });
        } else {
          this.tl.fromTo(el, { yPercent: 100 }, { yPercent: -15 });
          this.tl.addLabel(el.id);
        }
      });

      this.tl.fromTo(
        this.$refs.video,
        {
          currentTime: element.position,
        },
        {
          currentTime: this.hotspotGroups[index + 1]
            ? this.hotspotGroups[index + 1].position
            : videoTotalDuration,
          onReverseComplete: () => {
            gsap.to(element.hotspotsBtn, {
              opacity: 1,
              scale: 1,
              ease: 'power4.out',
              stagger: 0.05,
            });
            gsap.to(element.hotspotsContent, {
              rotate: 0,
              xPercent: 0,
              ease: 'power4.out',
              stagger: 0.1,
              duration: 0.2,
            });

            const nextElement = this.hotspotGroups[index + 1];
            if (nextElement) {
              gsap.to(nextElement.hotspotsBtn, {
                opacity: 0,
                scale: 0,
                ease: 'power4.out',
                stagger: 0.05,
              });
            }
          },
          onComplete: () => {
            const nextElement = this.hotspotGroups[index + 1];
            if (nextElement) {
              gsap.to(nextElement.hotspotsBtn, {
                opacity: 1,
                scale: 1,
                ease: 'power4.out',
                stagger: 0.05,
              });
              gsap.to(nextElement.hotspotsContent, {
                rotate: 0,
                xPercent: 0,
                ease: 'power4.out',
                stagger: 0.1,
                duration: 0.2,
              });
            }
          },
        },
      );
    });
  }

  /**
   *
   */
  initMobileTimeline() {
    const videoDuration = this.$refs.videoMobile.duration;
    this.hotspotGroups.forEach((element, index) => {
      if (index === 0) {
        this.tl.fromTo(
          this.$refs.videoMobile,
          {
            currentTime: 0,
          },
          {
            currentTime: element.position,
            onStart: () => {
              this.scrollIsStarted = true;
            },
            onUpdate: () => {
              if (this.$refs.videoMobile.currentTime < element.position && !element.isHide) {
                gsap.to(element.hotspotsBtn, {
                  opacity: 0,
                  scale: 0,
                  ease: 'power4.out',
                  stagger: 0.05,
                });
                element.isHide = true;
                element.disableGroup();
              }
              if (
                this.$refs.videoMobile.currentTime >= element.position &&
                element.isHide !== false
              ) {
                element.isHide = false;
                gsap.to(element.hotspotsBtn, {
                  opacity: 1,
                  scale: 1,
                  ease: 'power4.out',
                  stagger: 0.05,
                });
                element.enableGroup();
              }
            },
          },
        );
      }

      this.tl.to('#null', {
        duration: 2,
        onComplete: () => {
          gsap.to(element.hotspotsBtn, {
            opacity: 0,
            scale: 0,
            ease: 'power4.out',
            stagger: 0.05,
          });
        },
      });

      const nextDuration = this.hotspotGroups[index + 1]
        ? this.hotspotGroups[index + 1].position
        : videoDuration;

      this.tl.fromTo(
        this.$refs.videoMobile,
        {
          currentTime: element.position,
        },
        {
          currentTime: nextDuration,
          onReverseComplete: () => {
            gsap.to(element.hotspotsBtn, {
              opacity: 1,
              scale: 1,
              ease: 'power4.out',
              stagger: 0.05,
            });

            const nextElement = this.hotspotGroups[index + 1];
            if (nextElement) {
              gsap.to(nextElement.hotspotsBtn, {
                opacity: 0,
                scale: 0,
                ease: 'power4.out',
                stagger: 0.05,
              });
              nextElement.disableGroup();
            }
          },
          onComplete: () => {
            const nextElement = this.hotspotGroups[index + 1];
            if (nextElement) {
              gsap.to(nextElement.hotspotsBtn, {
                opacity: 1,
                scale: 1,
                ease: 'power4.out',
                stagger: 0.05,
              });
              nextElement.enableGroup();
            }
          },
        },
      );
    });
  }

  /**
   * When clicking on hotspot, go to label on timeline
   * @param {string} label
   * @returns {void}
   */
  goToLabel(label) {
    gsap.to(window, { scrollTo: this.tl.scrollTrigger.labelToScroll(label) });
  }

  /**
   * Check if device is mobile
   * @returns {boolean}
   */
  get isMobile() {
    return this.breakpoints.includes(this.$services.get('resized').breakpoint);
  }
}
